import type { MetricPoint } from '@lizrd/contracts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Format an ISO date-only string ("2026-08-02") → "Aug 2" without TZ drift. */
function fmtDay(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${MONTHS[Number(m) - 1] ?? '?'} ${Number(d)}`;
}

/**
 * A tiny zero-dependency sparkline (inline SVG, theme-tokened) for daily metric
 * series (CT-S3), with a lightweight timeline footer — the start and end dates
 * plus the current value — so a bare line has a reference. Scales to its
 * container via a 0–100 viewBox. Degrades gracefully: a single point renders as
 * a marker, an empty series as a "collecting" hint.
 */
export function TrendChart({
  points,
  color = 'var(--iris)',
  height = 40,
  ariaLabel,
  formatValue,
}: {
  points: MetricPoint[];
  color?: string;
  height?: number;
  ariaLabel?: string;
  /** Renders the current value in the footer (e.g. usd or `n => `${n}/5``). */
  formatValue?: (n: number) => string;
}) {
  if (points.length === 0) {
    return (
      <div
        className="grid place-items-center rounded-sm bg-surface-2/40 text-[0.65rem] text-muted"
        style={{ height }}
      >
        Collecting — trend appears after your next sync
      </div>
    );
  }

  const W = 100;
  const H = 100;
  const pad = 6;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const n = points.length;

  // Map each point into the viewBox (y inverted; flat line centred when n===1).
  const x = (i: number) => (n === 1 ? W / 2 : pad + (i / (n - 1)) * (W - pad * 2));
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);

  const line = points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
  const area = `${pad},${H - pad} ${line} ${W - pad},${H - pad}`;
  const first = points[0]!;
  const last = points[n - 1]!;
  const sameDay = first.day === last.day;

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        role="img"
        aria-label={ariaLabel ?? 'trend'}
      >
        <polygon points={area} fill={color} opacity={0.12} />
        <polyline
          points={line}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <circle
          cx={x(n - 1)}
          cy={y(last.value)}
          r={2.5}
          fill={color}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Timeline reference: start date · current value · end date. */}
      <div className="mt-1 flex items-center justify-between text-[0.6rem] text-muted">
        <span>{fmtDay(first.day)}</span>
        {formatValue && <span className="tnum text-haze">{formatValue(last.value)}</span>}
        {!sameDay && <span>{fmtDay(last.day)}</span>}
      </div>
    </div>
  );
}

/** First→last delta of a series (absolute + percent), or null if <2 points. */
export function seriesDelta(points: MetricPoint[]): { abs: number; pct: number | null } | null {
  if (points.length < 2) return null;
  const first = points[0]!.value;
  const last = points[points.length - 1]!.value;
  const abs = last - first;
  const pct = first !== 0 ? Math.round((abs / first) * 100) : null;
  return { abs, pct };
}
